import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

const legacyProductDetailsPattern = /<h4>Factual Performance<\/h4><ul>[\s\S]*?<\/ul>/i;
const updatedProductDetailsHtml =
  '<h4>Why Customers Order</h4><ul><li><b>Fast production time:</b> 1-3 days.</li><li><b>Free tracked delivery:</b> Included on every order.</li><li><b>Free preview before shipping:</b> Review your design before dispatch.</li></ul>';

function replaceLegacyProductDetails(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll('.product-accordion .accordion-content').forEach((content) => {
    const currentHtml = String(content.innerHTML || '');
    if (!legacyProductDetailsPattern.test(currentHtml)) return;
    const nextHtml = currentHtml.replace(legacyProductDetailsPattern, updatedProductDetailsHtml);
    if (nextHtml !== currentHtml) content.innerHTML = nextHtml;
  });
}

function scheduleLegacyProductDetailsReplacement() {
  replaceLegacyProductDetails();
  window.requestAnimationFrame(() => replaceLegacyProductDetails());
  window.setTimeout(() => replaceLegacyProductDetails(), 250);
  window.setTimeout(() => replaceLegacyProductDetails(), 1000);
}

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    replaceLegacyProductDetails(this.ownerDocument || document);

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleLegacyProductDetailsReplacement, { once: true });
} else {
  scheduleLegacyProductDetailsReplacement();
}

window.addEventListener('load', scheduleLegacyProductDetailsReplacement, { once: true });
